import { isSavedPaymentMethodComplete } from '@frontend/payments-platform';
import {
  FetchPaymentMethods,
  findPaymentMethod,
  isPaymentFieldType,
  PAYMENT_KIND,
  toFieldValue,
  toPaymentValue,
  toSavedPaymentMethod,
} from '../page-contents/filling-experience/components/dynamic-form-container/fields/payment-on-file-field/payment-field.helpers';
import type { DynamicFormField } from '../page-contents/filling-experience/components/dynamic-form-container/types';

type DraftFieldsMap = Record<string, Pick<DynamicFormField, 'meta'>>;

export interface DraftPaymentRestoreResult {
  answers: Record<string, unknown>;
  failedKeys: string[];
}

interface RestoreDraftPaymentMethodsArgs {
  answers: Record<string, unknown>;
  formFieldsMap: DraftFieldsMap;
  fetchPaymentMethods: FetchPaymentMethods;
}

/** Reloads masked details for draft payment references; unverifiable ones are dropped and listed in `failedKeys`. */
export const restoreDraftPaymentMethods = async ({
  answers,
  formFieldsMap,
  fetchPaymentMethods,
}: RestoreDraftPaymentMethodsArgs): Promise<DraftPaymentRestoreResult> => {
  const refs = Object.entries(answers).flatMap(([key, value]) => {
    const type = formFieldsMap[key]?.meta.type;
    if (!type || !isPaymentFieldType(type)) return [];
    const draftValue = toPaymentValue(type, value);
    if (!draftValue.paymentMethodId) return [];
    const nameOnCard = 'nameOnCard' in draftValue ? draftValue.nameOnCard : '';
    return [{ key, type, paymentMethodId: draftValue.paymentMethodId, nameOnCard }];
  });
  if (!refs.length) return { answers, failedKeys: [] };

  const response = await fetchPaymentMethods([...new Set(refs.map((ref) => ref.paymentMethodId))]).catch(
    () => undefined
  );

  const restored = { ...answers };
  const failedKeys: string[] = [];

  refs.forEach(({ key, type, paymentMethodId, nameOnCard }) => {
    const method = findPaymentMethod(response, paymentMethodId);
    const saved = method && toSavedPaymentMethod(method, PAYMENT_KIND[type]);

    if (saved && isSavedPaymentMethodComplete(saved)) {
      restored[key] = toFieldValue(saved, nameOnCard);
    } else {
      delete restored[key];
      failedKeys.push(key);
    }
  });

  return { answers: restored, failedKeys };
};
